import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Sparkle, ClockCounterClockwise } from "@phosphor-icons/react";

const OPTIONS = [
    { k: "spending", label: "Spending pattern review" },
    { k: "portfolio", label: "Portfolio & allocation" },
    { k: "board_paper", label: "Monthly board paper" },
    { k: "daily_auto", label: "Today's auto-insight", scheduled: true },
];

export default function Insights() {
    const [items, setItems] = useState([]);
    const [busy, setBusy] = useState(null);
    const [focusFilter, setFocusFilter] = useState("all");

    const load = async () => setItems((await api.get("/ai/insights")).data);
    useEffect(() => { load(); }, []);

    const gen = async (focus) => {
        setBusy(focus);
        try {
            if (focus === "daily_auto") {
                await api.post("/scheduled/daily-insight");
            } else {
                await api.post("/ai/insight", { focus });
            }
            await load();
        }
        finally { setBusy(null); }
    };

    const filtered = focusFilter === "all"
        ? items
        : items.filter(i => i.focus === focusFilter);

    // Group board papers by month
    const boardPapers = items.filter(i => i.focus === "board_paper");
    const monthGroups = boardPapers.reduce((acc, i) => {
        const m = new Date(i.created_at).toLocaleString("en-AU", { month: "long", year: "numeric" });
        (acc[m] = acc[m] || []).push(i);
        return acc;
    }, {});

    return (
        <div>
            <div className="mb-8">
                <div className="label-eyebrow mb-2">AI Insights</div>
                <h1 className="font-serif text-4xl lg:text-5xl tracking-tight">A quiet second opinion.</h1>
                <p className="text-[var(--muted)] mt-3 max-w-2xl">
                    Powered by Claude Sonnet 4.5. Reads your live snapshot, budget and portfolio —
                    then writes a short, plain-English commentary in the tone of a family CFO.
                </p>
            </div>

            <div className="grid md:grid-cols-4 gap-4 mb-8">
                {OPTIONS.map(o => (
                    <button key={o.k} disabled={busy === o.k}
                            onClick={() => gen(o.k)}
                            data-testid={`gen-${o.k}`}
                            className="hairline bg-white p-6 rounded text-left hover:bg-black/[0.03] transition-colors disabled:opacity-50">
                        <div className="flex items-center gap-2 mb-3 text-[var(--eucalyptus)]">
                            <Sparkle size={16} weight="fill" />
                            <span className="label-eyebrow" style={{ color: "var(--eucalyptus)" }}>Generate</span>
                        </div>
                        <div className="font-serif text-xl">{o.label}</div>
                        {busy === o.k && <div className="text-xs text-[var(--muted)] mt-3">Consulting the family CFO…</div>}
                    </button>
                ))}
            </div>

            {Object.keys(monthGroups).length > 0 && (
                <div className="mb-8">
                    <div className="label-eyebrow mb-3 flex items-center gap-2">
                        <ClockCounterClockwise size={14} /> Monthly Board Paper Archive
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {Object.entries(monthGroups).map(([m, arr]) => (
                            <span key={m} className="chip status-on-plan" data-testid={`month-${m.replace(/\s+/g, '-')}`}>
                                {m} · {arr.length}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            <div className="flex items-center gap-2 mb-4" data-testid="focus-filter">
                {[["all", "All"], ...OPTIONS.map(o => [o.k, o.label])].map(([k, l]) => (
                    <button key={k} onClick={() => setFocusFilter(k)}
                            className={`px-3 py-1.5 rounded text-xs border ${focusFilter === k
                                ? "bg-[var(--eucalyptus)] text-[var(--bone)] border-[var(--eucalyptus)]"
                                : "border-[var(--line)] hover:bg-black/5"}`}>
                        {l}
                    </button>
                ))}
            </div>

            <div className="space-y-6">
                {filtered.map(it => (
                    <div key={it.id} className="hairline bg-white p-8 rounded" data-testid="insight-card">
                        <div className="flex items-center justify-between mb-4">
                            <div className="label-eyebrow">{it.focus.replace("_", " ")}</div>
                            <div className="text-xs text-[var(--muted)]">{new Date(it.created_at).toLocaleString("en-AU")}</div>
                        </div>
                        <div className="prose prose-sm max-w-none whitespace-pre-wrap font-[Manrope] text-[var(--ink)] leading-relaxed">
                            {it.text}
                        </div>
                    </div>
                ))}
                {filtered.length === 0 && (
                    <div className="text-center text-[var(--muted)] py-16">
                        No insights yet. Generate one above.
                    </div>
                )}
            </div>
        </div>
    );
}
